import React from "react";
import { Button, Link } from "@heroui/react";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { useTrialSignupHref } from "../../lib/signupUrl";
import BasicNavbar from "./BasicNavbar";
import HeroDemoVideo from "./HeroDemoVideo";
import SideRays from "./SideRays";
import BrandsSection from "./BrandsSection";
import TargetIndustriesSection from "./TargetIndustriesSection";
import LatestNewsSection from "./LatestNewsSection";
import PainPointsSection from "./PainPointsSection";
// import GrowthPathsSection from "./GrowthPathsSection";
// import CoreCapabilitiesSection from "./CoreCapabilitiesSection";
import ProductMatrixSection from "./ProductMatrixSection";
import WorkAgentSection from "./WorkAgentSection";
import TestimonialsSection from "./TestimonialsSection";
import InvestorSection from "./InvestorSection";
import PricingSection from "./PricingSection";
import FAQSection from "./FAQSection";
import FooterCTASection from "../../components/FooterCTASection";
import SiteFooter from "../../components/SiteFooter";

type HomePageProps = {
  onBack?: () => void;
};

export default function HomePage({ onBack }: HomePageProps) {
  const heroTrialSignupHref = useTrialSignupHref("hero");

  return (
    <div className="dark">
      <div
        className="bg-background relative flex min-h-screen min-h-dvh w-full flex-col overflow-x-hidden scroll-smooth "
        style={{ ["--navbar-height" as string]: "60px" } as React.CSSProperties}
      >
      {onBack && (
        <Button
          className="fixed top-4 left-4 z-50"
          size="sm"
          variant="flat"
          onPress={onBack}
        >
          返回
        </Button>
      )}
      <BasicNavbar />
      <div id="home" className="relative flex min-h-dvh flex-1 flex-col">
      <main className="mt-[calc(80px+var(--intl-notice-height,0px))] w-full min-w-0 max-w-full shrink-0 px-5 sm:px-8">
        <section className="relative z-20 mx-auto mt-[clamp(48px,12dvh,100px)] flex w-full min-w-0 max-w-6xl flex-col items-start justify-center gap-[18px] sm:gap-6">
          <LazyMotion features={domAnimation}>
            <m.div
              animate="kick"
              className="flex flex-col gap-6"
              exit="auto"
              initial="auto"
              transition={{
                duration: 0.25,
                ease: "easeInOut",
              }}
              variants={{
                auto: { width: "auto" },
                kick: { width: "auto" },
              }}
            >
              <AnimatePresence mode="wait">
                <div className="flex flex-col items-start gap-3 sm:gap-4">
                  <m.p
                    key="hero-section-kicker"
                    animate={{
                      filter: "blur(0px)",
                      opacity: 1,
                      x: 0,
                    }}
                    className="flex items-center text-[18px] font-medium tracking-wide text-[#00b7ff] max-sm:text-[]"
                    initial={{
                      filter: "blur(16px)",
                      opacity: 0,
                      x: 15,
                    }}
                    transition={{
                      bounce: 0,
                      delay: 0.01 * 4,
                      duration: 0.8 + 0.1 * 6,
                      type: "spring",
                    }}
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-full bg-[#00b7ff] shadow-[0_0_8px_rgba(0,183,255,0.65)]"
                      aria-hidden
                    />
                    3Chat.ai 是以成交为导向的 AI 销冠智能体
                  </m.p>
                  <m.div
                    key="hero-section-title"
                    animate={{
                      filter: "blur(0px)",
                      opacity: 1,
                      x: 0,
                    }}
                    className="text-start text-[28px] leading-[1.2] font-bold tracking-tighter sm:text-[56px]"
                    initial={{
                      filter: "blur(16px)",
                      opacity: 0,
                      x: 15 + 1 * 2,
                    }}
                    transition={{
                      bounce: 0,
                      delay: 0.01 * 10,
                      duration: 0.8 + 0.1 * 8,
                      type: "spring",
                    }}
                  >
                    <div className="bg-hero-section-title bg-clip-text text-transparent">
                    让每一次客户对话，都更接近成交
                    </div>
                  </m.div>
                </div>

                <m.div
                  key="hero-section-description"
                  animate={{
                    filter: "blur(0px)",
                    opacity: 1,
                    x: 0,
                  }}
                  className="text-default-500 text-start leading-7 font-normal sm:w-[600px] sm:text-[18px]"
                  initial={{
                    filter: "blur(16px)",
                    opacity: 0,
                    x: 15 + 1 * 3,
                  }}
                  transition={{
                    bounce: 0,
                    delay: 0.01 * 30,
                    duration: 0.8 + 0.1 * 9,
                    type: "spring",
                  }}
                >
                  帮助拥有大量客户资产或高价值线索的 B2C 企业<br />
                  在关键对话中理解客户、判断下一步并执行业务动作
                </m.div>

                <m.div
                  key="hero-section-buttons"
                  animate={{
                    filter: "blur(0px)",
                    opacity: 1,
                    x: 0,
                  }}
                  className="flex w-full flex-row self-stretch items-stretch gap-3 sm:items-center sm:gap-6"
                  initial={{
                    filter: "blur(16px)",
                    opacity: 0,
                    x: 15 + 1 * 4,
                  }}
                  transition={{
                    bounce: 0,
                    delay: 0.01 * 50,
                    duration: 0.8 + 0.1 * 10,
                    type: "spring",
                  }}
                >
                  <Button
                    as={Link}
                    className="bg-default-foreground text-small text-background h-10 min-w-0 flex-1 basis-0 px-[16px] py-[10px] leading-5 font-medium transition-[background-color,color,box-shadow] duration-300 ease-out hover:bg-default-700 hover:text-background hover:shadow-lg hover:shadow-black/35 sm:w-[163px] sm:flex-none sm:basis-auto"
                    href={heroTrialSignupHref}
                    radius="full"
                  >
                    免费试用
                  </Button>
                  <Button
                    className="group border-foreground/20 text-small h-10 min-w-0 flex-1 basis-0 border-1 bg-foreground/10 px-[16px] py-[10px] leading-5 font-medium backdrop-blur-md transition-[background-color,color,box-shadow] duration-300 ease-out hover:border-foreground/20 hover:bg-foreground/30 hover:text-default-foreground data-[hover=true]:border-foreground/20 hover:shadow-md hover:shadow-black/25 sm:w-[163px] sm:flex-none sm:basis-auto"
                    endContent={
                      <span className="pointer-events-none flex h-[22px] w-[22px] items-center justify-center">
                        <ArrowRight
                          className="text-default-500 transition-[color] duration-300 ease-out group-hover:text-default-foreground [&>path]:stroke-[1.5]"
                          width={16}
                          height={16}
                        />
                      </span>
                    }
                    radius="full"
                    variant="bordered"
                    onPress={() => {
                      document.getElementById("product-matrix")?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest",
                      });
                    }}
                  >
                    查看产品如何工作
                  </Button>
                </m.div>
              </AnimatePresence>
            </m.div>
          </LazyMotion>
        </section>
      </main>
      <LazyMotion features={domAnimation}>
        <AnimatePresence mode="wait">
          <m.div
            key="hero-section-app-screenshot"
            animate={{
              filter: "blur(0px)",
              opacity: 1,
              y: 0,
            }}
            className="relative z-[12] mt-6 w-full min-w-0 max-w-full shrink-0 overflow-x-clip px-5 pb-10 sm:mt-10 sm:px-8 sm:pb-14 md:mt-14"
            initial={{
              filter: "blur(16px)",
              opacity: 0,
              y: 48,
            }}
            transition={{
              bounce: 0,
              delay: 0.12,
              duration: 2.35,
              type: "spring",
            }}
          >
            <div className="mx-auto w-full min-w-0 max-w-6xl origin-top scale-[0.92] transition-transform duration-300 lg:scale-100">
              <div className="overflow-hidden rounded-2xl border border-default-100/30 bg-black shadow-2xl shadow-black/40">
                <HeroDemoVideo />
              </div>
            </div>
          </m.div>
        </AnimatePresence>
      </LazyMotion>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[11] overflow-hidden select-none"
      >
        <SideRays
          speed={2.5}
          rayColor1="#EAB308"
          rayColor2="#68aaf4"
          intensity={2}
          spread={2}
          origin="top-right"
          tilt={0}
          saturation={1.5}
          blend={0.75}
          falloff={2}
          opacity={1}
        />
      </div>

      {/*
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[11] overflow-hidden select-none"
      >
        <div className="hero-irregular-glow">
          <div className="hero-irregular-glow__wash" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--core" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--cyan" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--cobalt" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--violet" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--mist" />
          <div className="hero-irregular-glow__blob hero-irregular-glow__blob--flare" />
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 top-[-25%] z-10 scale-150 select-none sm:scale-125">
        <FadeInImage
          alt="Gradient background"
          src="https://nextuipro.nyc3.cdn.digitaloceanspaces.com/components-images/backgrounds/bg-gradient.png"
        />
      </div>
      */}
      </div>

      <BrandsSection />
      <PainPointsSection />
      {/* <GrowthPathsSection /> */}
      {/* <CoreCapabilitiesSection /> */}
      <ProductMatrixSection />
      <WorkAgentSection />
      <TestimonialsSection />
      <InvestorSection />
      <TargetIndustriesSection />
      <PricingSection showFeatureComparison={false} />
      <LatestNewsSection />
      <FAQSection />
      <FooterCTASection />
      <SiteFooter className="mt-0" />
    </div>
    </div>
  );
}
